import "./Select.scss";

import React, {
	CSSProperties,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import clsx from "clsx";
import Ink from "react-ink";
import RcSelect, { Option } from "rc-select";

import * as strings from "../../../strings";
import { MAX_INPUT_LENGTH } from "../Field/Field";
import * as StringUtils from "../../../lib/util/StringUtils";
import { ViewContext, VisibleContext } from "../../../lib/util/ReactUtils";
import * as Utils from "../../../lib/util/Utils";
import { AboveNavContext } from "../../nav/Navigation/Navigation";
import { FaAngleDown, FaCircleNotch, FaTimes } from "react-icons/fa";
import {
	LocaleContext,
	TextElement,
	translate,
} from "../../../lib/util/LocaleUtils";
import Text from "../../common/Text/Text";
import InfoOverlay from "../../state/InfoOverlay/InfoOverlay";
import {
	useDebouncedCallback,
	useDebouncedMemo,
} from "../../../lib/hook/MiscHooks";

export interface SelectOption {
	value: any;
	label: TextElement;
	className?: string;
}

export interface SelectProps {
	id?: string;
	required?: boolean;
	placeholder?: string;
	value?: any;
	defaultValue?: any;
	multiple?: boolean;
	disabled?: boolean;
	options: SelectOption[];
	noClear?: boolean;
	label?: React.ReactNode;
	loading?: boolean;
	onRenderOption?: (option: SelectOption) => React.ReactNode;
	className?: string;
	readOnly?: boolean;
	tags?: boolean;
	onBlur?: () => void;
	compact?: boolean;
	small?: boolean;
	widget?: boolean;
	rounded?: boolean;
	fixed?: boolean;
	inline?: boolean;
	dev?: boolean;
	onChange?: (option?: SelectOption) => void;
	undefinedIsValue?: boolean;
	style?: CSSProperties;
	onSearch?: (value: string) => void;
}

class MixinSelect extends RcSelect {
	constructor(props: any) {
		super(props);
		this.forcePopupAlign = Utils.throttle(this.forcePopupAlign, 200);
	}
}

const selects = new Set();

const Select = (props: SelectProps) => {
	const ref = useRef<any>();
	const visibleContext = useContext(VisibleContext);

	const [uuid] = useState(StringUtils.uuid());
	const [visible, setVisible] = useState(false);

	const {
		id,
		required,
		placeholder,
		value: propsValue,
		defaultValue,
		multiple,
		options,
		noClear,
		label,
		onRenderOption,
		className,
		readOnly,
		tags,
		onBlur,
		disabled,
		loading,
		compact,
		small,
		widget,
		rounded,
		fixed,
		inline,
		dev,
		onChange,
		undefinedIsValue,
		onSearch,
		...otherProps
	} = props;

	const handleChange = useCallback(
		(_: any, el: any) => {
			if (onChange && !(readOnly || disabled)) {
				let data = null;
				if (Array.isArray(el)) {
					data = el.map((e) => {
						return e.props["data-option"] || { value: e.key };
					});
				} else {
					data = el ? el.props["data-option"] : null;
				}
				onChange(data);
			}
		},
		[disabled, onChange, readOnly],
	);

	const handleGetInput = useCallback(() => {
		return (
			<input
				autoComplete={"off"}
				maxLength={MAX_INPUT_LENGTH}
				tabIndex={-1}
				name={StringUtils.uuid(12)}
				id={uuid}
			/>
		);
	}, [uuid]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<any>) => {
			if (e.key === "Escape" && visible) {
				e.stopPropagation();
				e.preventDefault();
				return;
			}
			if (!Utils.isDebugHotkey(e) && Utils.isCharacterKeyPress(e) && !visible) {
				if (
					e.key.length === 1 &&
					ref.current.getInputElement() !== document.activeElement
				) {
					ref.current.setOpenState(true);
				}
			}
		},
		[visible],
	);

	useEffect(() => {
		selects.add(this);
		return () => {
			selects.delete(this);
		};
	}, []);

	const classes = useMemo(() => {
		return clsx({
			"--visible": visible,
			"--disabled": disabled,
			"--loading": loading,
			"--compact": compact,
			"--widget": widget,
			"--small": small,
			"--inline": inline,
			"--dev": dev,
			"--rounded": rounded,
			"--fixed": fixed,
			"--hide": !visibleContext,
		});
	}, [
		compact,
		dev,
		disabled,
		fixed,
		inline,
		loading,
		rounded,
		small,
		visible,
		visibleContext,
		widget,
	]);

	const handleFilterOption = useCallback((input: string, component: any) => {
		const option = component.props["data-option"];
		const searchInput = input && input.toString().toLowerCase();

		return (
			(option.label &&
				option.label.toString().toLowerCase().includes(searchInput)) ||
			(option.value &&
				option.value.toString().toLowerCase().includes(searchInput)) ||
			(option.tags &&
				option.tags.some((tag: any) =>
					tag.toString().toLowerCase().includes(searchInput),
				))
		);
	}, []);

	const renderOption = useCallback((onRenderOption, option) => {
		return onRenderOption
			? onRenderOption(option)
			: option.label || option.value;
	}, []);

	const renderNotFoundContent = useCallback((): any => {
		return (
			<div className={"Select__not-found"}>
				{loading ? (
					<InfoOverlay type={"loading"} heading={strings.MISC_LOADING} />
				) : (
					<InfoOverlay
						type={"noData"}
						heading={
							tags ? strings.FIELD_SELECT_ADD_TAGS : strings.MISC_NOTHING_FOUND
						}
					/>
				)}
			</div>
		);
	}, [loading, tags]);

	const renderOptions = useCallback(
		(options: SelectOption[], onRenderOption) => {
			return options.map((option) => {
				const content = (
					<>
						<Ink />
						{renderOption(onRenderOption, option)}
					</>
				);
				return (
					<Option
						key={option.value ? option.value : option.label}
						className={clsx("Select__item", option.className)}
						data-option={option}
						title={option.label || option.value}
						value={option.label || option.value}
					>
						{content}
					</Option>
				);
			});
		},
		[renderOption],
	);

	const [search, setSearch] = useState("");

	const value =
		propsValue === undefined && !undefinedIsValue ? defaultValue : propsValue;

	const useLazy = options.length > 20;

	const handleSearch = useDebouncedCallback(
		(value: string) => {
			setSearch(value);
			if (onSearch) {
				onSearch(value);
			}
		},
		[onSearch],
		200,
	);

	const handleDropdownVisibleChange = useCallback(
		(visible: any) => {
			setVisible(visible);
			if (visible && ref.current) {
				ref.current.forcePopupAlign();
			}

			if (visible) {
				handleSearch(search);
			}
		},
		[handleSearch, search],
	);

	return (
		<LocaleContext.Consumer>
			{(languageContext) => {
				const translatedOptions = options.map((option) => ({
					...option,
					label: translate(option.label, languageContext.locale),
				}));

				let currentSelection =
					value === undefined && !undefinedIsValue
						? value
						: translatedOptions &&
						  translatedOptions.find((opt) => opt.value === value);
				if (value !== undefined && !undefinedIsValue && !currentSelection) {
					currentSelection = {
						value: value,
						label: value,
					};
				}

				return (
					<ViewContext.Consumer>
						{(refGetter) => {
							return (
								<AboveNavContext.Consumer>
									{(aboveNav) => (
										<div
											{...otherProps}
											role={"none"}
											className={clsx("Select", className, classes)}
											onBlur={onBlur}
											onKeyDown={handleKeyDown}
										>
											{label && (
												<label htmlFor={id || uuid}>
													<Text inline={true}>{label}</Text>
													{required && (
														<span className={"Field__required-highlight"}>
															*
														</span>
													)}
												</label>
											)}
											<MixinSelect
												ref={ref}
												tags={tags}
												onSearch={handleSearch}
												getPopupContainer={refGetter}
												onDropdownVisibleChange={handleDropdownVisibleChange}
												notFoundContent={renderNotFoundContent()}
												loading={loading}
												// @ts-ignore
												useLazy={useLazy}
												dropdownPlacement={"bottom"}
												disabled={readOnly || disabled}
												filterOption={handleFilterOption}
												onChange={handleChange}
												dropdownClassName={clsx(
													"Select__dropdown",
													classes,
													aboveNav && "--above-nav",
													`Select__dropdown-${uuid}`,
												)}
												allowClear={!required && !noClear}
												getInputElement={handleGetInput}
												multiple={multiple}
												className={"Select__input"}
												clearIcon={<FaTimes className={"Select__clear"} />}
												inputIcon={
													loading ? (
														<FaCircleNotch className={"Select__loading"} />
													) : (
														<FaAngleDown className={"Select__arrow"} />
													)
												}
												placeholder={translate(
													loading
														? strings.CONTROLS_SELECT_PLACEHOLDER_LOADING
														: placeholder ||
																strings.CONTROLS_SELECT_PLACEHOLDER,
													languageContext.locale,
												)}
												value={
													(currentSelection &&
														(currentSelection.label ||
															currentSelection.value)) ||
													undefined
												}
											>
												{loading
													? null
													: renderOptions(translatedOptions, onRenderOption)}
											</MixinSelect>
										</div>
									)}
								</AboveNavContext.Consumer>
							);
						}}
					</ViewContext.Consumer>
				);
			}}
		</LocaleContext.Consumer>
	);
};

export default React.memo(Select);
